import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../auth/AuthProvider";
import { useTranslation } from "react-i18next";
import Sidebar from "../components/Sidebar";
import { useCompany } from "../hooks/useCompany";
import { fillForm1096Bytes, mergeCopyABytes } from "../components/Form1099PDF";

const API_URL = process.env.REACT_APP_API_URL;

const currentYear = new Date().getFullYear();
const yearOptions = Array.from({ length: 5 }, (_, i) => currentYear - i);

const fmtAmount = (n) =>
  Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const filingToFormData = (f) => ({
  taxYear: f.tax_year,
  payerLine1: f.payer_line1 || "",
  payerLine2: f.payer_line2 || "",
  payerLine3: [
    [f.payer_city, f.payer_state].filter(Boolean).join(", "),
    f.payer_zip,
    f.payer_phone,
  ].filter(Boolean).join("  "),
  payerTIN: f.payer_tin || "",
  recipientName: f.recipient_name || "",
  recipientAddress: f.recipient_address || "",
  recipientCityLine: [
    [f.recipient_city, f.recipient_state].filter(Boolean).join(", "),
    f.recipient_zip,
  ].filter(Boolean).join("  "),
  recipientTIN: "",
  box1: fmtAmount(f.box1),
  box4: fmtAmount(f.box4),
});

const to1096Data = (f) => ({
  payerLine1:   f.payer_line1  || "",
  payerLine2:   f.payer_line2  || "",
  payerLine3:   [[f.payer_city, f.payer_state].filter(Boolean).join(", "), f.payer_zip]
                  .filter(Boolean).join("  "),
  payerTIN:     f.payer_tin    || "",
  ssn:          f.ssn          || "",
  contactName:  f.contact_name  || "",
  contactPhone: f.contact_phone || "",
  contactEmail: f.contact_email || "",
  faxNumber:    f.fax_number   || "",
  totalForms:   String(f.total_forms || 0),
  box1:         fmtAmount(f.total_amount),    // Box 5
  box4:         fmtAmount(f.total_withheld),  // Box 4
});

const Filings1096List = () => {
  const auth = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { activeCompany } = useCompany();

  const [taxYear, setTaxYear] = useState(String(currentYear - 1));
  const [filing1096, setFiling1096] = useState(null);
  const [filings1099, setFilings1099] = useState([]);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    if (!auth?.isAuthenticated || !activeCompany) return;
    setLoading(true);
    const headers = { Authorization: `Bearer ${auth.user?.access_token}` };
    const cid = activeCompany.company_id;

    Promise.all([
      fetch(`${API_URL}/filings_1096?company_id=${cid}&tax_year=${taxYear}`, { headers })
        .then((r) => (r.ok ? r.json() : null)).catch(() => null),
      fetch(`${API_URL}/filings_1099?company_id=${cid}&tax_year=${taxYear}`, { headers })
        .then((r) => (r.ok ? r.json() : [])).catch(() => []),
    ]).then(([f1096, f1099s]) => {
      setFiling1096(f1096);
      setFilings1099(Array.isArray(f1099s) ? f1099s : []);
      setLoading(false);
    });
  }, [auth.isAuthenticated, auth.user?.access_token, activeCompany, taxYear]);

  const handleRedownload = async () => {
    if (!filing1096) return;
    setDownloading(true);
    try {
      // 1. Re-generate 1096 PDF
      const bytes1096 = await fillForm1096Bytes(to1096Data(filing1096));
      const blob1096 = new Blob([bytes1096], { type: "application/pdf" });
      const url1096 = URL.createObjectURL(blob1096);
      const a1096 = document.createElement("a");
      a1096.href = url1096;
      a1096.download = `1096-${taxYear}.pdf`;
      a1096.click();
      URL.revokeObjectURL(url1096);

      // 2. Re-generate merged Copy A pages
      if (filings1099.length > 0) {
        const formDataList = filings1099.map(filingToFormData);
        const mergedBytes = await mergeCopyABytes(formDataList);
        const blobA = new Blob([mergedBytes], { type: "application/pdf" });
        const urlA = URL.createObjectURL(blobA);
        const aA = document.createElement("a");
        aA.href = urlA;
        aA.download = `1099s-CopyA-${taxYear}.pdf`;
        aA.click();
        URL.revokeObjectURL(urlA);
      }
    } finally {
      setDownloading(false);
    }
  };

  const handleSignOut = () => {
    auth.signOut();
  };

  return (
    <div className="flex min-h-screen bg-gray-100">
      <Sidebar handleSignOut={handleSignOut}>
        <div className="flex flex-col flex-1 p-3 sm:p-6">
          <div className="bg-white shadow-lg rounded-lg p-4 sm:p-8 w-full">

            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-1">
              <h1 className="text-xl sm:text-2xl font-semibold text-gray-800">1096 History</h1>
              <button
                onClick={() => navigate("/Generate1096")}
                className="bg-green-500 text-white px-4 py-2 rounded-lg hover:bg-green-600 transition text-sm font-medium w-full sm:w-auto"
              >
                Generate / Edit 1096
              </button>
            </div>
            <p className="text-gray-500 text-sm mb-6">
              Saved Form 1096 for the selected tax year.
            </p>

            {/* Year filter */}
            <div className="mb-6 flex items-center gap-3">
              <label className="text-sm font-medium text-gray-600">Tax Year</label>
              <select
                value={taxYear}
                onChange={(e) => setTaxYear(e.target.value)}
                className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-green-400"
              >
                {yearOptions.map((y) => <option key={y} value={y}>{y}</option>)}
              </select>
            </div>

            {/* Content */}
            {loading ? (
              <div className="flex items-center justify-center gap-2 py-10">
                <svg className="animate-spin h-5 w-5 text-green-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                <span className="text-gray-400 text-sm">Loading 1096 filing…</span>
              </div>
            ) : !filing1096 ? (
              <div className="text-center py-12">
                <p className="text-gray-400 text-lg mb-4">No 1096 saved for {taxYear}.</p>
                <button
                  onClick={() => navigate("/Generate1096")}
                  className="bg-green-500 text-white px-6 py-2 rounded-lg hover:bg-green-600 text-sm"
                >
                  Generate 1096
                </button>
              </div>
            ) : (
              <div className="space-y-6">

                {/* Summary stats */}
                <div className="flex flex-wrap gap-4">
                  <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-3 min-w-[130px]">
                    <p className="text-blue-700 font-medium text-xs">Forms Filed (Box 3)</p>
                    <p className="text-xl font-bold text-blue-800">{filing1096.total_forms ?? 0}</p>
                  </div>
                  <div className="bg-green-50 border border-green-200 rounded-lg px-4 py-3 min-w-[160px]">
                    <p className="text-green-700 font-medium text-xs">Total Reported (Box 5)</p>
                    <p className="text-xl font-bold text-green-800">${fmtAmount(filing1096.total_amount)}</p>
                  </div>
                  {parseFloat(filing1096.total_withheld) > 0 && (
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg px-4 py-3 min-w-[160px]">
                      <p className="text-yellow-700 font-medium text-xs">Fed. Tax Withheld (Box 4)</p>
                      <p className="text-xl font-bold text-yellow-800">${fmtAmount(filing1096.total_withheld)}</p>
                    </div>
                  )}
                </div>

                {/* Detail card */}
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <div className="bg-gray-50 px-4 py-2 border-b border-gray-200">
                    <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Filing Details</span>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3 px-4 py-4 text-sm">
                    <div>
                      <p className="text-xs text-gray-400 font-medium uppercase">Filer Name</p>
                      <p className="text-gray-800">{filing1096.payer_line1 || "—"}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-400 font-medium uppercase">Address</p>
                      <p className="text-gray-800">
                        {[filing1096.payer_line2, filing1096.payer_city, filing1096.payer_state, filing1096.payer_zip]
                          .filter(Boolean).join(", ") || "—"}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-400 font-medium uppercase">EIN</p>
                      <p className="text-gray-800">{filing1096.payer_tin || "—"}</p>
                    </div>
                    {filing1096.ssn && (
                      <div>
                        <p className="text-xs text-gray-400 font-medium uppercase">SSN (Box 2)</p>
                        <p className="text-gray-800">{filing1096.ssn}</p>
                      </div>
                    )}
                    <div>
                      <p className="text-xs text-gray-400 font-medium uppercase">Contact Name</p>
                      <p className="text-gray-800">{filing1096.contact_name || "—"}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-400 font-medium uppercase">Contact Phone</p>
                      <p className="text-gray-800">{filing1096.contact_phone || "—"}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-400 font-medium uppercase">Contact Email</p>
                      <p className="text-gray-800">{filing1096.contact_email || "—"}</p>
                    </div>
                    {filing1096.fax_number && (
                      <div>
                        <p className="text-xs text-gray-400 font-medium uppercase">Fax</p>
                        <p className="text-gray-800">{filing1096.fax_number}</p>
                      </div>
                    )}
                    <div>
                      <p className="text-xs text-gray-400 font-medium uppercase">Last Updated</p>
                      <p className="text-gray-800">
                        {filing1096.updated_at ? new Date(filing1096.updated_at).toLocaleDateString() : "—"}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-400 font-medium uppercase">Accompanying 1099s</p>
                      <p className="text-gray-800">{filings1099.length} recipient{filings1099.length !== 1 ? "s" : ""}</p>
                    </div>
                  </div>
                </div>

                {/* Download button */}
                <button
                  onClick={handleRedownload}
                  disabled={downloading}
                  className="bg-green-500 text-white px-6 py-2.5 rounded-lg hover:bg-green-600 transition text-sm font-semibold disabled:opacity-50"
                >
                  {downloading
                    ? "Generating…"
                    : `↓ Re-download 1096 + Copy A (${filings1099.length} 1099${filings1099.length !== 1 ? "s" : ""})`}
                </button>

              </div>
            )}

            <div className="mt-6">
              <button
                onClick={() => navigate("/")}
                className="bg-gray-500 text-white py-2 px-6 rounded-lg hover:bg-gray-600 transition text-sm"
              >
                {t("back_to_dashboard_btn")}
              </button>
            </div>

          </div>
        </div>
      </Sidebar>
    </div>
  );
};

export default Filings1096List;
